/**
 * „Mit KI bearbeiten“, the one dialog every editing page opens: the passage —
 * the marked one, or a whole field when nothing is marked —, an instruction,
 * the model's suggestion (which the editor may change) and „Übernehmen“ to
 * put it in place. The page says how the model is asked (`suggest`) and what
 * „Übernehmen“ does (`onAccept`).
 *
 * Mounted per edit, so every dialog starts empty; closed while the model is
 * still writing, its answer is dropped.
 */

import { Sparkles } from 'lucide-react'
import { useId, useState } from 'react'

import { toast } from '../utils/toast'
import { AI_EDIT_FAILED, failureMessage } from './editor/api'
import Button from './ui/Button'
import Modal from './ui/Modal'
import { cn, INPUT, LABEL } from './ui/styles'

interface AiEditModalProps {
    /** The text the suggestion replaces. */
    passage: string
    /** Set when nothing was marked: the name of the field the suggestion replaces as a whole. */
    wholeField?: string
    /** The model's version of `passage`; a failure is an `ApiError` or `SessionExpiredError` (`editor/api.ts`). */
    suggest: (instruction: string) => Promise<string>
    onClose: () => void
    /** The suggestion as the editor left it. */
    onAccept: (replacement: string) => void
}

export default function AiEditModal({ passage, wholeField, suggest, onClose, onAccept }: AiEditModalProps) {
    const id = useId()
    const [instruction, setInstruction] = useState('')
    const [suggestion, setSuggestion] = useState<string | null>(null)
    const [busy, setBusy] = useState(false)

    const run = async () => {
        if (busy || !instruction.trim() || !passage.trim()) return
        setBusy(true)
        try {
            setSuggestion(await suggest(instruction))
        } catch (err) {
            const message = failureMessage(err, AI_EDIT_FAILED)
            if (message) toast.error(message)
        } finally {
            setBusy(false)
        }
    }

    return (
        <Modal
            open
            onClose={onClose}
            title="Mit KI bearbeiten"
            description={
                wholeField
                    ? `Nichts markiert: Der Vorschlag ersetzt das ganze Feld „${wholeField}“.`
                    : 'Der Vorschlag ersetzt die markierte Stelle.'
            }
            icon={Sparkles}
            footer={
                <>
                    <Button variant="ghost" onClick={onClose}>
                        Abbrechen
                    </Button>
                    <Button onClick={() => suggestion && onAccept(suggestion)} disabled={!suggestion}>
                        Übernehmen
                    </Button>
                </>
            }
        >
            <div className="space-y-5">
                <div>
                    <p id={`${id}-passage`} className={LABEL}>
                        {wholeField ? `Ganzes Feld: ${wholeField}` : 'Markierter Text'}
                    </p>
                    <blockquote
                        aria-labelledby={`${id}-passage`}
                        className="max-h-32 overflow-y-auto whitespace-pre-wrap rounded-lg border border-line bg-surface-subtle p-3 text-sm italic text-fg"
                    >
                        {passage ? `„${passage}“` : <span className="text-fg-muted">Dieses Feld ist leer.</span>}
                    </blockquote>
                </div>

                <div>
                    <label htmlFor={`${id}-instruction`} className={LABEL}>
                        Anweisung
                    </label>
                    <div className="flex gap-2">
                        <input
                            id={`${id}-instruction`}
                            type="text"
                            className={INPUT}
                            placeholder="z. B. „kürzer fassen“ oder „Grammatik korrigieren“"
                            value={instruction}
                            onChange={(event) => setInstruction(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    event.preventDefault()
                                    void run()
                                }
                            }}
                        />
                        <Button
                            variant="secondary"
                            onClick={() => void run()}
                            disabled={!instruction.trim() || !passage.trim()}
                            loading={busy}
                            className="shrink-0"
                        >
                            {busy ? 'Läuft …' : 'Vorschlag erstellen'}
                        </Button>
                    </div>
                </div>

                {suggestion !== null && (
                    <div className="animate-fadeIn">
                        <label htmlFor={`${id}-suggestion`} className={LABEL}>
                            Vorschlag (bearbeitbar)
                        </label>
                        <textarea
                            id={`${id}-suggestion`}
                            className={cn(INPUT, 'min-h-[150px] text-base leading-relaxed')}
                            value={suggestion}
                            onChange={(event) => setSuggestion(event.target.value)}
                        />
                    </div>
                )}
            </div>
        </Modal>
    )
}
